// Hover help: programs, a home patient's programs and schedules, and home requirements.

import { CardOutlineMini } from "./mini";
import { DistancePicture, EyeOrderPicture, LevelsPicture, SchedulePicture } from "./pictures";
import { ChromeAllowPicture, CordPicture, HelperPicture } from "../components/SetupPictures";
import type { HelpContext, HelpEntry } from "./types";

const m = (body: string) => `<math display="block">${body}</math>`;
const mi = (s: string) => `<mi>${s}</mi>`;
const mn = (s: string | number) => `<mn>${s}</mn>`;
const mo = (s: string) => `<mo>${s}</mo>`;

const steps = (ctx: HelpContext) => (ctx.program?.steps ?? []).map((s) => s.name || s.experiment.kind);
const order = (ctx: HelpContext) => (
  <EyeOrderPicture order={ctx.program?.eye_order ?? "eye_by_eye"} eyes={ctx.program?.eyes ?? ["OD", "OS"]}
    steps={steps(ctx).length ? steps(ctx) : ["Acuity", "Contrast"]} />
);

const LEVELS = "Each can be mandatory (must pass), recommended (shown with how to fix it; the patient may go on, and the "
  + "results carry a note) or not required (not asked).";

export const PROGRAM_HELP: Record<string, HelpEntry> = {
  "program.steps": {
    title: "Experiments, in order",
    what: "What a session runs: each experiment, a set number of times, in this order, for each eye. Each step copies the "
      + "experiment's settings when the program is saved, so editing an experiment later does not change the program until "
      + "you bring it up to date (the Experiments page lists programs on an older version). A contrast experiment sized "
      + "from acuity needs an acuity experiment before it.",
    picture: order,
    scenarios: [
      { scenario: "Standard home monitoring", value: "Acuity (QUEST) → Contrast (4 staircases)", why: "about 100 trials per eye" },
      { scenario: "Test-retest within a session", value: "Contrast × 2", why: "two estimates to compare" },
    ],
  },
  "program.eyes": {
    title: "Eyes",
    what: "Which eyes are tested, each with the other covered: right (OD), left (OS), or both together (OU). The session runs "
      + "every step for each.",
    picture: order,
  },
  "program.eye_order": {
    title: "Order",
    what: "Eye by eye (default): all the steps for the right eye, then all for the left: the patient moves the cover once. "
      + "Step by step: each experiment for both eyes before the next: the two eyes are tested closer in time, so fatigue "
      + "affects them alike.",
    picture: order,
  },
  "program.viewing_distance_mm": {
    title: "Viewing distance",
    what: "How far the patient sits from the screen (the cord in the kit is cut to it). Letter sizes in degrees and logMAR "
      + "are computed from it: farther away, each pixel is a smaller angle, so smaller letters can be drawn, but the largest "
      + "letter is smaller too.",
    math: m(`${mi("θ")}${mo("=")}${mn(2)}<mspace width="0.1em"/>${mi("arctan")}<mfrac><mi>h</mi><mrow>${mn(2)}${mi("d")}</mrow></mfrac>`),
    mathNote: "θ: the angle a letter of height h subtends at distance d.",
    picture: (ctx) => <DistancePicture mm={ctx.program?.viewing_distance_mm ?? 1000} />,
    scenarios: [
      { scenario: "Home, 27-inch 4K monitor", value: "1 m", why: "about 110 px per degree: 1-pixel strokes near −0.3 logMAR" },
      { scenario: "Clinic, large screen", value: "3–4 m", why: "finer letters; matches ETDRS distances" },
    ],
  },
  "plan.overview": {
    title: "Programs at home",
    what: "A home patient can have several programs, in order, each with how often it should be done. The mode decides "
      + "what a session runs. Programs done in one sitting (a visit) share one screen check and one brightness reading; "
      + "after each, the results page offers the next one due.",
  },
  "plan.mode": {
    title: "How a session picks",
    what: "Staff choose the active one: the program marked active, every session.\n\nBy schedule: every program that is "
      + "due, one after another, in order; programs on \"patient decides\" are offered as extras.\n\nPatient picks: the "
      + "patient chooses one of their programs each time.\n\nAll, back to back: every program, every session, in order.",
    scenarios: [
      { scenario: "One program", value: "Staff choose the active one", why: "simplest" },
      { scenario: "Weekly contrast, monthly colour", value: "By schedule", why: "each runs when due; staff see what is overdue" },
      { scenario: "Exploratory studies", value: "Patient picks", why: "the patient chooses; the history records which" },
    ],
  },
  "plan.schedule": {
    title: "How often",
    what: "Patient decides (unmonitored): never due, never overdue. Every N sessions: in every N-th visit. Every N days, "
      + "weeks or months: due that long after it was last done (or from when it was given); staff see it as overdue "
      + "after the grace days.",
    picture: () => <SchedulePicture kind="interval" n={2} unit="weeks" grace={3} />,
    scenarios: [
      { scenario: "Weekly home monitoring", value: "every 1 week, grace 2 days", why: "a missed week shows up after Wednesday" },
      { scenario: "Less frequent extra test", value: "every 3 sessions", why: "comes round regardless of dates" },
    ],
  },
  "req.overview": {
    title: "Home requirements",
    what: "What this patient's first-time setup asks for, and what every home session checks. " + LEVELS + " A patient without "
      + "their own requirements takes them from their program's home checks.",
    picture: () => <LevelsPicture />,
  },
  "req.setup": {
    title: "First-time setup",
    what: "Opens by itself at the patient's first sign-in, and again after staff ask for it; the patient can open it any "
      + "time. Mandatory steps must be done before the first test; recommended steps may be skipped (the skip is recorded).",
    picture: () => <LevelsPicture />,
  },
  "req.session": {
    title: "Every session",
    what: "Checked before each visit's first test (later programs in the visit reuse them). " + LEVELS
      + " A desktop with WebGL2 and a known screen size are always needed, and so is another monitor's identity (it gives the size).",
    picture: () => <LevelsPicture />,
  },
  "req.setup.chrome": {
    title: "Setup: Chrome and its permission",
    what: "The test needs Google Chrome (version 122 or newer), WebGL2 and a float16 drawing buffer, and Chrome's permission "
      + "to see the screens (window management), which names the screen so it can be matched to an approved monitor.",
    picture: () => <ChromeAllowPicture />,
  },
  "req.setup.monitor_helper": {
    title: "Setup: identify the monitor",
    what: "The patient downloads and runs Check my monitor, which reads every connected monitor's EDID (maker, model, serial, "
      + "size, 10-bit input; on a Mac also the bits sent) and sends it to the study. Needed for another (unapproved) "
      + "monitor's size; for approved models it confirms the unit.",
    picture: () => <HelperPicture />,
  },
  "req.setup.display": {
    title: "Setup: display settings and brightness",
    what: "Native resolution, HDR off and the given picture mode, then the screen check and the full lux-meter guide; the "
      + "reading becomes this monitor's brightness baseline, which every session's reading is compared with (±10%).",
  },
  "req.setup.card_size": {
    title: "Setup: size check with a bank card",
    what: "An outline the size of a bank card (85.60 × 53.98 mm) drawn from the screen's known size; the patient makes it "
      + "match a real card held to the screen. Catches screen magnifiers, the monitor's zoom or overscan, and a wrong size "
      + "on record: the browser cannot see any of these.",
    math: m(`<mo>|</mo>${mi("s")}${mo("−")}${mn(1)}<mo>|</mo>${mo("≤")}${mn("0.05")}`),
    mathNote: "s: the outline size the patient matched, relative to the drawn one.",
    picture: () => <CardOutlineMini />,
  },
  "req.setup.practice": {
    title: "Setup: cord, keypad and practice",
    what: "Sitting at the cord's length, pressing each arrow key once, and five large Es with feedback (not kept as results).",
    picture: () => <CordPicture metres={1} />,
  },
  "req.session.screen": {
    title: "Screen check",
    what: "Chrome 122+, WebGL2, float16, HDR off (no PQ), the screen named, native resolution, a computer type the model was "
      + "checked with, refresh ≥ 50 Hz. Recommended: failures are shown with their fixes but do not stop the test (results "
      + "carry a note). Not required: recorded only.",
  },
  "req.session.monitor.identity": { title: "Monitor identity: read", what: "This screen is a monitor Check my monitor has read (matched by name and resolution)." },
  "req.session.monitor.model": { title: "Monitor identity: model", what: "Its EDID maker and product code are the approved model's." },
  "req.session.monitor.serial": { title: "Monitor identity: serial", what: "It is the monitor sent to this patient (the serial set on the patient page). Applies only when a serial is set." },
  "req.session.monitor.panel_bits": { title: "Monitor identity: 10-bit input", what: "The monitor declares 10 bits per colour in its EDID (older EDIDs do not say; that never fails)." },
  "req.session.monitor.link_bits": { title: "Monitor identity: 10 bits sent", what: "macOS reports the bits it sends to the monitor; fewer than 10 limits faint contrast (about log CS 2.05 at 8 bits, against 2.65 at 10)." },
  "req.session.brightness": {
    title: "Brightness",
    what: "One lux-meter reading of the white screen per visit, within ±10% of this monitor's baseline. Recommended: the "
      + "patient may skip it (contrast results then carry a note).",
  },
  "req.session.distance": {
    title: "Distance",
    what: "The patient ticks that they sit at the program's distance, measured with the cord. Recommended: the box is shown "
      + "but the test can start without it (noted). Not required: not asked.",
  },
  "req.session.card_size": {
    title: "Card check every visit",
    what: "The bank-card size check before each visit's first test (later programs in the visit reuse it).",
    picture: () => <CardOutlineMini />,
  },
};
